import { Home as HomeIcon, Video, Baby, Briefcase, Check, ArrowUpRight, MessageCircle, CalendarDays, Ear, Eye, Sparkles, ListChecks, Heart, Star } from "lucide-react";
import SEO from "@/lib/seo";
import Reveal, { MaskedLine } from "@/components/Reveal";
import { useSettings } from "@/lib/settings";
import { useContent, Em, CmsImg } from "@/lib/content";
import { formatPrice } from "@/lib/format";


const CONSULT_META = [
  { id: "domicile", icon: HomeIcon, bg: "bg-mint/20", wa: "une consultation allaitement à domicile" },
  { id: "visio", icon: Video, bg: "bg-rose/30", wa: "une consultation allaitement en visio" },
];
const INCLUDED_ICONS = [Ear, Eye, Sparkles, ListChecks, Heart];
const ATELIER_META = [
  { id: "prenatal", icon: Baby, num: "01", wa: "l'atelier de préparation prénatale à l'allaitement" },
  { id: "reprise", icon: Briefcase, num: "02", wa: "l'atelier de préparation à la reprise du travail" },
];

function useWa() {
  const settings = useSettings();
  const { t } = useContent();
  const number = (settings.whatsapp || "").replace(/\D/g, "");
  return (subject) => `https://wa.me/${number}?text=${encodeURIComponent(t("svc.wa_message", { sujet: subject }))}`;
}

function WaButton({ href, children, testid, light = false }) {
  const settings = useSettings();
  const booking = (settings.booking_url || "").trim();
  const cls = `inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium transition-all active:scale-[0.98] ${light ? "bg-cream text-teal-dark hover:bg-white" : "bg-teal text-cream hover:bg-teal-dark shadow-[0_12px_30px_rgba(16,114,105,0.25)]"}`;
  if (!booking) {
    return (
      <a data-testid={testid} href={href} target="_blank" rel="noopener noreferrer" className={cls}>
        <MessageCircle size={16} /> {children}
      </a>
    );
  }
  return (
    <span className="inline-flex flex-wrap items-center gap-x-4 gap-y-2">
      <a data-testid={testid} href={booking} target="_blank" rel="noopener noreferrer" className={cls}>
        <CalendarDays size={16} /> {children}
      </a>
      <a data-testid={`${testid}-wa`} href={href} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-1.5 text-xs font-medium underline-offset-4 hover:underline ${light ? "text-cream/80" : "text-teal"}`}>
        <MessageCircle size={13} /> ou par WhatsApp
      </a>
    </span>
  );
}

function PriceTag({ price, duration, testid, light = false }) {
  return (
    <div data-testid={testid} className={`inline-flex items-baseline gap-2 rounded-2xl px-4 py-2.5 ${light ? "bg-cream/10 border border-cream/15" : "bg-white/80 border border-line"}`}>
      <span className={`font-serif text-2xl ${light ? "text-mint" : "text-teal"}`}>{formatPrice(price)}</span>
      {duration && <span className={`text-xs ${light ? "text-cream/70" : "text-ink/60"}`}>• {duration}</span>}
    </div>
  );
}

export default function Services() {
  const wa = useWa();
  const settings = useSettings();
  const { t, list } = useContent();
  const consults = CONSULT_META.map((c) => ({ ...c, tag: t(`svc.${c.id}.tag`), title: t(`svc.${c.id}.title`), text: t(`svc.${c.id}.text`), ideal: t(`svc.${c.id}.ideal`) }));
  const included = list("svc.included").map((text, i) => ({ text, icon: INCLUDED_ICONS[i % INCLUDED_ICONS.length] }));
  const ateliers = ATELIER_META.map((a) => ({ ...a, title: t(`svc.${a.id}.title`), text: t(`svc.${a.id}.text`), gains: list(`svc.${a.id}.gains`) }));
  return (
    <div data-testid="services-page">
      <SEO
        title="Consultations & ateliers allaitement à Dakar — Esther, conseillère en lactation"
        description="Consultations allaitement à domicile à Dakar ou en visio, ateliers de préparation prénatale et de reprise du travail. Esther, conseillère en lactation Tendres Tétées, vous accompagne sans jugement. Rendez-vous par WhatsApp."
        image={t("img.svc.hero")}
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-cream-light pt-36 pb-16 md:pb-24">
        <span aria-hidden className="absolute -top-24 right-0 h-[26rem] w-[26rem] rounded-full bg-mint/25 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <p className="eyebrow mb-5">{t("svc.eyebrow")}</p>
            <h1 className="font-serif text-teal-dark text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              <MaskedLine delay={0.1}>{t("svc.title1")}</MaskedLine>
              <MaskedLine delay={0.25}><Em>{t("svc.title2")}</Em></MaskedLine>
            </h1>
            <Reveal delay={0.4}>
              <p className="mt-7 text-ink/80 leading-relaxed max-w-2xl">
                {t("svc.intro1")}
              </p>
              <p className="mt-4 text-ink/70 leading-relaxed max-w-2xl">
                {t("svc.intro2")}
              </p>
              <div className="mt-9 flex flex-wrap items-center gap-4">
                <WaButton testid="services-hero-cta" href={wa("une consultation allaitement")}>{t("svc.hero_cta")}</WaButton>
                <a href="#ateliers" data-testid="services-hero-ateliers" className="link-underline text-sm font-medium text-teal">Découvrir les ateliers</a>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.3} className="lg:col-span-5">
            <div className="relative">
              <CmsImg imgKey="img.svc.hero" alt="Maman allaitant son bébé accompagnée par une conseillère en lactation à Dakar" fetchPriority="high" className="rounded-[2.5rem] overflow-hidden w-full h-[26rem]" />
              <div className="absolute -bottom-5 -left-4 md:-left-8 rounded-3xl bg-white border border-line px-5 py-4 shadow-[0_20px_50px_rgba(16,114,105,0.12)]">
                <div className="flex items-center gap-1 text-teal">{["a", "b", "c", "d", "e"].map((k) => <Star key={`star-${k}`} size={13} className="fill-teal" />)}</div>
                <p className="mt-1 font-serif text-xl text-teal-dark">{t("svc.stat_title")}</p>
                <p className="text-xs text-ink/60">{t("svc.stat_sub")}</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* POURQUOI */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-5">
            <p className="eyebrow mb-4">{t("svc.why.eyebrow")}</p>
            <h2 className="font-serif text-3xl md:text-5xl text-teal-dark leading-tight">
              <Em>{t("svc.why.title")}</Em>
            </h2>
            <p className="mt-6 text-ink/75 leading-relaxed">
              {t("svc.why.text")}
            </p>
          </Reveal>
          <div className="lg:col-span-7">
            <p className="font-mono text-[11px] uppercase tracking-widest text-mauve mb-5">Je vous aide à</p>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {list("svc.help").map((h, i) => (
                <Reveal key={h} delay={i * 0.06} as="li">
                  <div className="flex items-start gap-3 rounded-3xl bg-cream-light p-5 h-full">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-teal text-cream"><Check size={13} /></span>
                    <p className="text-sm text-ink/85 leading-relaxed">{h}</p>
                  </div>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CONSULTATIONS */}
      <section id="consultations" className="py-20 md:py-28 bg-cream scroll-mt-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Reveal className="max-w-2xl mb-12">
            <p className="eyebrow mb-4">{t("svc.consult.eyebrow")}</p>
            <h2 className="font-serif text-3xl md:text-5xl text-teal-dark leading-tight"><Em>{t("svc.consult.title")}</Em></h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {consults.map((c, i) => {
              const Icon = c.icon;
              return (
                <Reveal key={c.id} delay={i * 0.1}>
                  <article data-testid={`service-${c.id}`} className={`h-full rounded-[2rem] ${c.bg} p-7 md:p-9 flex flex-col`}>
                    <div className="flex items-center gap-3">
                      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-teal"><Icon size={22} /></span>
                      <p className="font-mono text-[11px] uppercase tracking-widest text-teal-dark">{c.tag}</p>
                    </div>
                    <h3 className="mt-6 font-serif text-2xl md:text-3xl text-teal-dark">{c.title}</h3>
                    <p className="mt-4 text-ink/80 leading-relaxed">{c.text}</p>
                    <p className="mt-5 text-sm text-ink/70"><span className="font-medium text-teal-dark">Idéal pour :</span> {c.ideal}</p>
                    <div className="mt-8 pt-2 mt-auto flex flex-wrap items-center gap-4">
                      <PriceTag testid={`service-${c.id}-price`} price={settings[`svc_${c.id}_price`]} duration={settings[`svc_${c.id}_duration`]} />
                      <WaButton testid={`service-${c.id}-cta`} href={wa(c.wa)}>{t("svc.book")}</WaButton>
                    </div>
                  </article>
                </Reveal>
              );
            })}
          </div>

          <Reveal className="mt-14 rounded-[2rem] bg-white border border-line p-7 md:p-10">
            <p className="font-serif text-2xl text-teal-dark">{t("svc.included.title")}</p>
            <ul className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5">
              {included.map((it) => {
                const Icon = it.icon;
                return (
                  <li key={it.text} className="flex lg:flex-col items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-mint/30 text-teal"><Icon size={18} /></span>
                    <p className="text-sm text-ink/80 leading-relaxed">{it.text}</p>
                  </li>
                );
              })}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* ATELIERS */}
      <section id="ateliers" className="py-20 md:py-28 bg-teal text-cream scroll-mt-24 relative overflow-hidden">
        <span aria-hidden className="text-outline font-serif italic absolute -bottom-10 right-0 text-[18vw] leading-none select-none pointer-events-none opacity-40">Ateliers</span>
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <Reveal className="max-w-2xl mb-12">
            <p className="eyebrow !text-mint mb-4">{t("svc.ateliers.eyebrow")}</p>
            <h2 className="font-serif text-3xl md:text-5xl leading-tight"><Em className="text-mint">{t("svc.ateliers.title")}</Em></h2>
          </Reveal>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {ateliers.map((a, i) => {
              const Icon = a.icon;
              return (
                <Reveal key={a.id} delay={i * 0.1}>
                  <article data-testid={`atelier-${a.id}`} className="h-full rounded-[2rem] bg-teal-dark/60 border border-cream/10 p-7 md:p-9 flex flex-col">
                    <div className="flex items-center justify-between">
                      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-cream text-teal"><Icon size={22} /></span>
                      <span className="font-serif italic text-4xl text-mint/70">{a.num}</span>
                    </div>
                    <h3 className="mt-6 font-serif text-2xl md:text-3xl">{a.title}</h3>
                    <p className="mt-4 text-cream/80 leading-relaxed">{a.text}</p>
                    <p className="mt-6 font-mono text-[11px] uppercase tracking-widest text-mint">{t("svc.gains_label")}</p>
                    <ul className="mt-3 space-y-2.5">
                      {a.gains.map((g) => (
                        <li key={g} className="flex items-start gap-2.5 text-sm text-cream/90"><Check size={15} className="mt-0.5 shrink-0 text-mint" />{g}</li>
                      ))}
                    </ul>
                    <div className="mt-8 mt-auto pt-6 flex flex-wrap items-center gap-4">
                      <PriceTag light testid={`atelier-${a.id}-price`} price={settings[`svc_${a.id}_price`]} duration={settings[`svc_${a.id}_duration`]} />
                      <WaButton light testid={`atelier-${a.id}-cta`} href={wa(a.wa)}>{t("svc.book_atelier")}</WaButton>
                    </div>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-20 md:py-28 bg-rose/50">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-7">
            <p className="eyebrow mb-4">{t("svc.final.eyebrow")}</p>
            <h2 className="font-serif text-3xl md:text-6xl text-teal-dark leading-tight"><Em>{t("svc.final.title")}</Em></h2>
            <p className="mt-6 text-ink/75 max-w-xl leading-relaxed">
              {t("svc.final.text")}
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <WaButton testid="services-final-cta" href={wa("une consultation ou un atelier allaitement")}>{t("svc.final.cta")}</WaButton>
              <a href="/boutique" data-testid="services-final-shop" className="inline-flex items-center gap-1.5 rounded-full border border-teal px-6 py-3 text-sm font-medium text-teal hover:bg-teal hover:text-cream transition-colors">
                {t("svc.final.shop")} <ArrowUpRight size={15} />
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5">
            <CmsImg imgKey="img.svc.final" alt="Atelier de préparation à l'allaitement à Dakar" className="rounded-[2.5rem] overflow-hidden w-full h-72 md:h-96" />
          </Reveal>
        </div>
      </section>
    </div>
  );
}
